/* eslint-disable */
import React, { ReactNode, forwardRef, useId, useImperativeHandle, useMemo, useState } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import { Element } from '@ckeditor/ckeditor5-engine';
// eslint-disable-next-line import/no-extraneous-dependencies
import Node from '@ckeditor/ckeditor5-engine/src/model/node';
// eslint-disable-next-line import/no-extraneous-dependencies
import Text from '@ckeditor/ckeditor5-engine/src/model/text';
// eslint-disable-next-line import/no-extraneous-dependencies
// eslint-disable-next-line import/extensions, import/no-unresolved
import 'react-contexify/ReactContexify.css';
import { UpdateElementHandler } from './SectionDataTreeNodeInterface';
import { ValidationResults } from './ValidationResults';

interface DataTreeTextNodeProps {
  element: Text;
  selectedElement: Node | undefined;
  handleSelectClick: (node: Node, e: React.MouseEvent) => void;
  updateComponent: (ancestor: number) => void;
  ghost: boolean;
  isDragging: boolean;
  showContextMenu: (node: Node, e: React.MouseEvent) => void;
  viewAttributes: string[];
  validationResults: ValidationResults;
}

const SectionDataTreeTextNode = forwardRef<UpdateElementHandler, DataTreeTextNodeProps>(
  (
    {
      element,
      selectedElement,
      handleSelectClick,
      updateComponent,
      ghost,
      isDragging,
      showContextMenu,
      viewAttributes,
      validationResults,
    },
    ref
  ) => {
    const id = useId();

    /**
     * 更新用ステート変数。
     */
    const [counter, setCounter] = useState<number>(0);
    const updateComponentLocal = (ancestor: number) => {
      let localAncestor = ancestor;

      localAncestor -= 1;
      if (localAncestor >= 0) {
        if (localAncestor === 0) {
          setCounter(counter + 1);
        }
        updateComponent(ancestor);
      }
    };
    const updateParentComponent = () => {
      updateComponentLocal(2);
    };

    /**
     * 要素の属性値一覧を取得する。
     */
    const elementAttributes = Array.from(element.getAttributes()).reduce((acc, current) => {
      const name = current[0];
      const value = current[1] as unknown;
      if (/Attributes$/i.test(name)) {
        const lowAttributes = (value as any).attributes;
        if (lowAttributes) {
          Object.assign(acc, lowAttributes);
        }
      } else {
        const toAppend = {} as Record<string, unknown>;
        toAppend[name] = value;
        Object.assign(acc, toAppend);
      }

      return acc;
    }, {} as { [key: string]: any });
    if (element === selectedElement) {
      // デバッグ用
    }

    /**
     * 右クリックイベントハンドラ（onContextMenu）。
     * @param e イベントパラメータ。
     */
    const onContextMenu = async (e: React.MouseEvent) => {
      showContextMenu(element, e);
    };

    /**
     * 文字列に対して属性値を反映させる。
     * @param data 元になる文字列。
     * @returns 属性値を反映させた結果。
     */
    const applyAttributes = (data: string): [ReactNode, string] => {
      let result = <>&quot;{data}&quot;</>;
      let titleString = `"${data}"`;

      const attributeNames = {
        bold: 'strong',
        underline: 'u',
        italic: 'i',
        fontColor: 'span',
        superscript: 'sup',
        subscript: 'sub',
      } as Record<string, string>;

      Object.keys(attributeNames).forEach((key) => {
        if (elementAttributes[key]) {
          result = (
            <>
              <strong>{attributeNames[key]}</strong> {result}
            </>
          );
          if (key === 'fontColor') {
            titleString = `<span style="color:: ${elementAttributes.fontColor as string}"> ${titleString}`;
          } else {
            titleString = `<${attributeNames[key]}> ${titleString}`;
          }
        }
      });

      // `html`で始まる属性を適用
      Object.keys(elementAttributes).forEach((key) => {
        if (key.startsWith('html')) {
          const elementName = key.slice(4).toLowerCase();
          const elementData = elementAttributes[key];

          const displayAttributesString = viewAttributes.reduce((acc, current) => {
            if (key === 'htmlA' && current === 'href') {
              const attributeValue = elementAttributes.linkHref;
              if (attributeValue) {
                acc += ` @href="${attributeValue}"`;
              }
            } else {
              const attributeValue = elementData.attributes?.[current];
              if (attributeValue) {
                acc += ` @${current}="${attributeValue}"`;
              }
            }

            return acc;
          }, '');

          result = (
            <>
              <strong>{elementName}</strong>
              {elementData &&
                elementData?.classes &&
                elementData?.classes.length > 0 &&
                `.${elementData?.classes.join('.')}`}
              {displayAttributesString}
              {` `}
              {result}
            </>
          );
          titleString = `<${elementName}${elementData?.classes ? `.${elementData?.classes.join('.')}` : ''}${
            elementData?.attributes?.id ? ` id="${elementData?.attributes?.id}"` : ''
          }${
            elementName === 'a' && elementAttributes.linkHref ? ` href="${elementAttributes.linkHref}"` : ''
          }> ${titleString}`;
        }
      });

      return [result, titleString];
    };
    const [data, title] = useMemo(() => applyAttributes(element.data), [element.data]);

    /**
     * 外部から操作できる関数。
     */
    useImperativeHandle(ref, () => ({
      /**
       * 指定された要素を更新する。
       * @param target 対象要素。
       */
      updateElement: (target: Element) => {
        if (target === (element as Node)) {
          updateParentComponent();
        }
      },

      /**
       * 指定された要素以降の最初のテキスト要素を検索する。
       * @param target 対象要素。
       * @param found 対象要素が見つかったら以降はtrueを渡す。
       * @returns テキスト要素。存在しなければundefined。後ろはfoundと同じ。
       */
      searchTextElement: (target: Element, found: boolean): [Text | undefined, boolean] => {
        let localFound = found;
        if (!localFound && (target as unknown as Text) === element) {
          localFound = true;
        }

        return localFound ? [element, localFound] : [undefined, localFound];
      },

      /**
       * 指定された要素配下の開閉状態を変更する。
       * @param target 対象要素（対象要素を含む配下が対象）。
       * @param found 開く場合はtrue、閉じる場合はfalseを渡す。
       * @returns なし。
       */
      openCloseNode: (target: Node, isTarget: boolean, open: boolean) => {
        // empty
      },
    }));

    /**
     * 表示幅を調整するための文字列。
     */
    const displayWidthString = '　'.repeat(100);

    return (
      <div
        className={`section-data-tree-node text-node${element === selectedElement ? ' selected' : ''}${
          isDragging ? ' is-dragging' : ''
        }${ghost ? ' ghost' : ''}`}
      >
        {!ghost && (
          <>
            <input
              type="checkbox"
              checked={false}
              onChange={() => {
                // empty
              }}
            />
            <span className="text-node-icon" />
          </>
        )}
        <label onClick={(e) => handleSelectClick(element, e)} onContextMenu={onContextMenu} title={title}>
          {data}
          {!ghost && <span style={{ color: 'transparent' }}>{displayWidthString}</span>}
        </label>
      </div>
    );
  }
);
export { SectionDataTreeTextNode };
